import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "ghost" | "danger";
type Size = "md" | "sm" | "xs";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-primary text-white border border-primary/40 hover:brightness-110 hover:-translate-y-px",
  ghost: "bg-bg3/80 text-text2 border border-border hover:border-border2 hover:bg-bg4 hover:text-text",
  danger: "bg-danger-dim text-danger border border-danger/20 hover:bg-danger/15 hover:border-danger/35",
};

// Plancher tactile (pointer:coarse) SANS toucher le desktop : min-height gagne
// sur height, donc les boutons gardent leur densité à la souris et montent à
// 40/36px au doigt. Pas de règle globale 44px : elle gonflerait tables et tabs.
const SIZES: Record<Size, string> = {
  md: "h-9 px-3.5 text-[13px] [@media(pointer:coarse)]:min-h-10",
  sm: "h-7 px-2.5 text-[11.5px] [@media(pointer:coarse)]:min-h-9",
  xs: "h-6 px-2 text-[11px] [@media(pointer:coarse)]:min-h-9",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-sm font-semibold whitespace-nowrap transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-0",
        "disabled:opacity-50 disabled:pointer-events-none",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  ),
);
Button.displayName = "Button";
